import { Fragment, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { useAuthStore } from "@/store/auth";
import { PageLoader } from "@/components/ui/Spinner";
import { formatAmount } from "@/utils/formatAmount";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import type { Project, FinancialSummary } from "@/features/enterprises/types";
import { ProjectStatusBadge } from "@/features/enterprises/components/ContractStatusBadge";
import { CalcMethodBadge } from "@/features/finances/components/CalcMethodBadge";
import { COST_METHOD, type CostMethod } from "@/features/finances/types";

import { ProjectStatusControl } from "@/features/finances/components/ProjectStatusControl";
import { canDeactivateProject, canSetProjectStatus } from "@/features/enterprises/permissions";
import { ClientInvoicesPanel } from "@/features/finances/components/ClientInvoicesPanel";
import { InternalCostsPanel } from "@/features/finances/components/InternalCostsPanel";
import { PlannedRevenuesPanel } from "@/features/finances/components/PlannedRevenuesPanel";
import { EmployeeAllocationsReadOnlyPanel } from "@/features/finances/components/EmployeeAllocationsReadOnlyPanel";
import { CashflowChart } from "@/features/finances/components/CashflowChart";
import {
  useFinancialSummary,
  useCashflowEvents,
  useEngagementCurve,
  useNarzutPeopleBreakdown,
  useNarzutFixedBreakdown,
} from "@/features/finances/hooks/useFinances";
import { Info, X } from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";

type Tab =
  | "cashflow"
  | "revenues"
  | "costs"
  | "salary"
  | "planned"
  | "comparison";

export function ProjectFinancePage() {
  const { id } = useParams<{ id: string }>();
  const user = useAuthStore((s) => s.user);
  const [tab, setTab] = useState<Tab>("cashflow");
  const [openRevenueId, setOpenRevenueId] = useState<string | null>(null);
  const [method, setMethod] = useState<CostMethod>(COST_METHOD.PROPORTIONAL);

  const { data: projects, isLoading } = useQuery({
    queryKey: ["projects", { project: id }],
    queryFn: () =>
      apiClient
        .get<{ data: Project[] }>("/projects/", { params: { project: id } })
        .then((r) => r.data.data),
    enabled: !!id,
  });
  const project = projects?.[0];
  usePageTitle(project && `Finanse: ${project.name}`);

  const { data: summary } = useFinancialSummary(id);
  const { data: summaryDirect } = useFinancialSummary(id, COST_METHOD.DIRECT);
  const { data: cashflowEvents = [], refetch: refetchCashflow } =
    useCashflowEvents(id, method);

  if (isLoading) return <PageLoader />;
  if (!project)
    return (
      <p className="text-gray-500 dark:text-gray-400">
        Nie znaleziono przedsięwzięcia.
      </p>
    );
  if (!project.has_finance_access)
    return <Navigate to={`/projects/${id}`} replace />;

  const canEdit = user?.role === "director" && project.has_finance_access;
  const canEditFinances =
    (user?.role === "director" || user?.role === "accountant") &&
    project.has_finance_access;
  // Status idzie osobną bramką: koordynator przypisany do przedsięwzięcia TAK,
  // księgowość NIE (mimo prawa do edycji kwot). Dezaktywacja ostrzej: CEO lub
  // dyrektor techniczny z dostępem finansowym.
  const canSetStatus = canSetProjectStatus(user, project);
  const canDeactivate = canDeactivateProject(user, project);

  const tabs: Array<{ id: Tab; label: string; shortLabel: string }> = [
    { id: "cashflow", label: "Wykres cashflow", shortLabel: "Cashflow" },
    { id: "revenues", label: "Faktury PRZ", shortLabel: "Faktury" },
    { id: "costs", label: "Koszty wewnętrzne", shortLabel: "Koszty" },
    { id: "planned", label: "Planowane przychody", shortLabel: "Przychody" },
    { id: "salary", label: "Płace i alokacje", shortLabel: "Płace" },
    {
      id: "comparison",
      label: "Porównanie: stary / nowy",
      shortLabel: "Stary/nowy",
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Link
              to={`/projects/${id}`}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800"
            >
              ← {project.short_name}
            </Link>
          </div>
          <div>
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
              {project.name}
            </h1>
            <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-6" />
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            {project.client_name}
          </p>
        </div>
        <ProjectStatusBadge project={project} />
      </div>

      {/* Status + value control */}
      {project.has_finance_access && (
        <div className="mb-6">
          <ProjectStatusControl project={project} canEdit={canEditFinances} canSetStatus={canSetStatus} canDeactivate={canDeactivate} />
        </div>
      )}

      {/* Switch metody liczenia + Summary strip */}
      <CostMethodSwitch method={method} onChange={setMethod} />
      {(() => {
        const active = method === COST_METHOD.DIRECT ? summaryDirect : summary;
        return active ? (
          <ProjectFinanceSummary summary={active} projectId={id} />
        ) : null;
      })()}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="flex gap-0.5 sm:gap-1 overflow-x-auto">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-2 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap flex-shrink-0 ${
                tab === t.id
                  ? "bg-white dark:bg-gray-800 border border-b-white border-gray-200 dark:border-gray-700 text-[var(--color-primary)] -mb-px"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-800"
              }`}
            >
              <span className="sm:hidden">{t.shortLabel}</span>
              <span className="hidden sm:inline">{t.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div>
        {tab === "cashflow" && (
          <CashflowChart
            events={cashflowEvents}
            onPlannedRevenueClick={(entityId) => {
              setOpenRevenueId(entityId);
              setTab("planned");
            }}
            onRefetch={refetchCashflow}
          />
        )}
        {tab === "revenues" && (
          <ClientInvoicesPanel
            projectId={project.id}
            canEdit={canEditFinances}
          />
        )}
        {tab === "costs" && (
          <InternalCostsPanel
            projectId={project.id}
            canEdit={canEditFinances}
          />
        )}
        {tab === "planned" && (
          <PlannedRevenuesPanel
            projectId={project.id}
            canEdit={canEdit}
            openId={openRevenueId}
          />
        )}
        {tab === "salary" && (
          <EmployeeAllocationsReadOnlyPanel projectId={project.id} />
        )}
        {tab === "comparison" && summary && (
          <ProjectFinanceComparison
            proportional={summary}
            direct={summaryDirect}
          />
        )}
      </div>
    </div>
  );
}

function ProjectFinanceComparison({
  proportional,
  direct,
}: {
  proportional: FinancialSummary;
  direct?: FinancialSummary;
}) {
  if (!direct) return <PageLoader />;
  const n = (v: string) => parseFloat(v) || 0;

  const rows: {
    label: string;
    o: string;
    n: string;
    bold?: boolean;
    signed?: boolean;
  }[] = [
    {
      label: "Koszty płac",
      o: proportional.salary_cost,
      n: direct.salary_cost,
    },
    {
      label: "Koszty stałe (cykliczne)",
      o: proportional.recurring_internal_cost,
      n: direct.recurring_internal_cost,
    },
    {
      label: "Koszty jednorazowe",
      o: proportional.one_time_internal_cost,
      n: direct.one_time_internal_cost,
    },
    {
      label: "Faktury podwykonawców",
      o: proportional.actual_contractor_cost,
      n: direct.actual_contractor_cost,
    },
    {
      // Po staremu = 0 (nieprojektowi + globalne stałe są rozsmarowane w płace/koszty stałe);
      // po nowemu = osobna, jawna pozycja narzutu wg wartości.
      label: "Narzut (nieprojektowi + ogólne wg wartości)",
      o: proportional.overhead_narzut,
      n: direct.overhead_narzut,
    },
    {
      label: "Koszt całkowity",
      o: proportional.total_actual_cost,
      n: direct.total_actual_cost,
      bold: true,
    },
    {
      label: "Bilans (przychód − koszt)",
      o: proportional.balance,
      n: direct.balance,
      bold: true,
      signed: true,
    },
  ];

  const chartData = [
    {
      k: "Płace",
      stary: n(proportional.salary_cost),
      nowy: n(direct.salary_cost),
    },
    {
      k: "Koszty stałe",
      stary: n(proportional.recurring_internal_cost),
      nowy: n(direct.recurring_internal_cost),
    },
    {
      k: "Jednorazowe",
      stary: n(proportional.one_time_internal_cost),
      nowy: n(direct.one_time_internal_cost),
    },
    {
      k: "Narzut",
      stary: n(proportional.overhead_narzut),
      nowy: n(direct.overhead_narzut),
    },
    {
      k: "Koszt razem",
      stary: n(proportional.total_actual_cost),
      nowy: n(direct.total_actual_cost),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-gray-500 dark:text-gray-400 max-w-3xl">
        To samo przedsięwzięcie policzone na dwa sposoby. <b>Po staremu</b>:
        płace zawierają rozbite wolne moce (niedociążeni ludzie spływają na
        projekt), a koszty stałe — udział w globalnych kosztach firmy.{" "}
        <b>Po nowemu</b>: projekt płaci tylko za ludzi faktycznie przypisanych i
        własne koszty — wolne moce i koszt obsługi firmy są pokazane osobno.
        Różnica to dokładnie to, co stary sposób „dosmarowywał".
      </p>

      {/* Tabela 1:1 */}
      <div className="bg-white dark:bg-gray-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-gray-400 border-b border-gray-100 dark:border-gray-700">
              <th className="px-4 py-3 font-medium">Pozycja</th>
              <th className="px-4 py-3 font-medium text-right">
                <div className="flex items-center justify-end gap-2">
                  <CalcMethodBadge kind="official" />
                </div>
              </th>
              <th className="px-4 py-3 font-medium text-right">
                <div className="flex items-center justify-end gap-2">
                  <CalcMethodBadge kind="analytical" />
                </div>
              </th>
              <th className="px-4 py-3 font-medium text-right">Różnica</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const diff = n(r.o) - n(r.n);
              return (
                <tr
                  key={r.label}
                  className={`border-b border-gray-50 dark:border-gray-700/50 ${r.bold ? "font-bold" : ""}`}
                >
                  <td className="px-4 py-2.5 text-gray-800 dark:text-gray-100">
                    {r.label}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-900 dark:text-gray-100">
                    {r.signed && n(r.o) >= 0 ? "+" : ""}
                    {formatAmount(r.o)} PLN
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-900 dark:text-gray-100">
                    {r.signed && n(r.n) >= 0 ? "+" : ""}
                    {formatAmount(r.n)} PLN
                  </td>
                  <td
                    className={`px-4 py-2.5 text-right tabular-nums ${Math.abs(diff) < 0.005 ? "text-gray-300 dark:text-gray-600" : "text-amber-700 dark:text-amber-400"}`}
                  >
                    {Math.abs(diff) < 0.005
                      ? "—"
                      : `${diff > 0 ? "−" : "+"}${formatAmount(Math.abs(diff).toFixed(2))} PLN`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Wykres 1:1 */}
      <div className="bg-white dark:bg-gray-800 p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
          Koszty: stary vs nowy
        </p>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart
            data={chartData}
            margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#e5e7eb"
              opacity={0.4}
            />
            <XAxis dataKey="k" tick={{ fontSize: 11 }} tickLine={false} />
            <YAxis
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              width={70}
            />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
              formatter={(value) =>
                `${formatAmount(Number(value).toFixed(2))} PLN`
              }
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar
              dataKey="stary"
              name="Po staremu (oficjalne)"
              fill="#10b981"
              radius={[3, 3, 0, 0]}
            />
            <Bar
              dataKey="nowy"
              name="Po nowemu (analityczne)"
              fill="#6366f1"
              radius={[3, 3, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function CostMethodSwitch({
  method,
  onChange,
}: {
  method: CostMethod;
  onChange: (m: CostMethod) => void;
}) {
  const opts: { v: CostMethod; label: string }[] = [
    {
      v: COST_METHOD.PROPORTIONAL,
      label: "Proporcjonalny (ze proporcjonalnym rozkładem wolnych mocy)",
    },
    { v: COST_METHOD.DIRECT, label: "Bezpośredni (tylko realne przydziały)" },
  ];
  return (
    <div className="mb-4 flex items-center gap-3 flex-wrap">
      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        Sposób liczenia kosztów
      </span>
      <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {opts.map((o) => (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
              method === o.v
                ? "bg-[var(--color-primary)] text-white"
                : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
      <CalcMethodBadge
        kind={method === COST_METHOD.DIRECT ? "analytical" : "official"}
      />
    </div>
  );
}

function ProjectFinanceSummary({
  summary,
  projectId,
}: {
  summary: import("@/features/enterprises/types").FinancialSummary;
  projectId?: string;
}) {
  const [narzutModal, setNarzutModal] = useState<null | "people" | "fixed">(
    null,
  );
  const balance = parseFloat(summary.balance);
  // Forecast and physical balance come from backend now — single source of truth,
  // no double-counting of issued PRZ FV (was: balance + planned_revenue_60d which counted issued FV twice).
  const forecast60d = parseFloat(summary.expected_cash_balance_60d);
  const physicalBalance = parseFloat(summary.current_cash_balance);
  // v2: use physical pay-out aliases — only what has actually left the bank account.
  // Accrued salary/recurring (not yet physically paid out) belongs to forecast, not here.
  const physicalInternalCosts =
    parseFloat(summary.paid_one_time_internal_cost) +
    parseFloat(summary.paid_recurring_to_today) +
    parseFloat(summary.paid_salary_to_today);

  const balancePositive = balance >= 0;
  const forecastPositive = forecast60d >= 0;
  const physicalPositive = physicalBalance >= 0;

  return (
    <div className="mb-6 flex flex-col gap-3">
      {/* Row 1 — three balance cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Current balance */}
        <div
          className={`rounded-xl border p-3 sm:p-5 ${balancePositive ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
            Stan konta zlecenia
          </p>
          <p
            className={`text-xl sm:text-3xl font-bold ${balancePositive ? "text-green-700" : "text-red-700"}`}
          >
            {balancePositive ? "+" : ""}
            {formatAmount(summary.balance)} PLN
          </p>
          <p className="hidden sm:block text-xs text-gray-500 mt-1">
            Przychody zafakturowane{" "}
            <strong className="text-green-700">
              +{formatAmount(summary.actual_revenue)} PLN
            </strong>{" "}
            minus wszystkie poniesione koszty{" "}
            <strong className="text-red-700">
              −{formatAmount(summary.total_actual_cost)} PLN
            </strong>
          </p>
        </div>

        {/* Physical balance */}
        <div
          className={`rounded-xl border p-3 sm:p-5 ${physicalPositive ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
            Stan konta zlecenia fizyczny
          </p>
          <p
            className={`text-xl sm:text-3xl font-bold ${physicalPositive ? "text-green-700" : "text-red-700"}`}
          >
            {physicalPositive ? "+" : ""}
            {formatAmount(physicalBalance.toFixed(2))} PLN
          </p>
          <p className="hidden sm:block text-xs text-gray-500 mt-1">
            Faktycznie wpłacone przez inwestora{" "}
            <strong className="text-green-700">
              +{formatAmount(summary.paid_revenue)} PLN
            </strong>{" "}
            minus faktycznie opłacone faktury P-GK{" "}
            <strong className="text-red-700">
              −{formatAmount(summary.paid_contractor_cost)} PLN
            </strong>
            {parseFloat(summary.official_fee_cost) > 0 && (
              <>
                {" "}
                i opłaty urzędowe{" "}
                <strong className="text-red-700">
                  −{formatAmount(summary.official_fee_cost)} PLN
                </strong>
              </>
            )}{" "}
            i koszty wewnętrzne{" "}
            <strong className="text-red-700">
              −{formatAmount(physicalInternalCosts.toFixed(2))} PLN
            </strong>
          </p>
        </div>

        {/* 60-day forecast */}
        <div
          className={`rounded-xl border p-3 sm:p-5 ${forecastPositive ? "bg-blue-50 border-blue-200" : "bg-red-50 border-red-200"}`}
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">
            Wirtualny stan konta za 60 dni
          </p>
          <p
            className={`text-xl sm:text-3xl font-bold ${forecastPositive ? "text-green-700" : "text-red-700"}`}
          >
            {forecastPositive ? "+" : ""}
            {formatAmount(forecast60d.toFixed(2))} PLN
          </p>
          <p className="hidden sm:block text-xs text-gray-500 mt-1">
            Aktualny stan kasowy{" "}
            <strong
              className={
                parseFloat(summary.current_cash_balance) >= 0
                  ? "text-green-700"
                  : "text-red-700"
              }
            >
              {parseFloat(summary.current_cash_balance) >= 0 ? "+" : ""}
              {formatAmount(summary.current_cash_balance)} PLN
            </strong>{" "}
            plus oczekiwane wpływy{" "}
            <strong className="text-green-700">
              +{formatAmount(summary.expected_inflows_60d)} PLN
            </strong>{" "}
            minus oczekiwane wypływy{" "}
            <strong className="text-red-700">
              −{formatAmount(summary.expected_outflows_60d)} PLN
            </strong>{" "}
            w ciągu 60 dni
          </p>
        </div>
      </div>

      {/* Row 2 — cost breakdown */}
      <div className="bg-white dark:bg-gray-800 p-3 sm:p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
          Struktura kosztów od początku projektu
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <CostItem
            label="Faktury podwykonawców"
            value={summary.actual_contractor_cost}
            hint="zaakceptowane faktury P-GK"
          />
          <CostItem
            label="Koszty jednorazowe"
            value={summary.one_time_internal_cost}
            hint="koszty wewnętrzne jednorazowe"
          />
          <CostItem
            label="Koszty stałe"
            value={summary.recurring_internal_cost}
            hint={
              parseFloat(summary.overhead_narzut) > 0
                ? "koszty cykliczne własne projektu"
                : "comiesięczne koszty przypisane i ogólne"
            }
          />
          <CostItem
            label="Koszty płac"
            value={summary.salary_cost}
            hint={
              parseFloat(summary.overhead_narzut) > 0
                ? "płace przydzielonych (jawne)"
                : "alokacje pracowników"
            }
          />
          {parseFloat(summary.overhead_narzut) > 0 && (
            <>
              <CostItem
                label="Narzut — nieprojektowi"
                value={summary.overhead_narzut_people}
                hint="płace kadr/księgowości/zarządu wg wartości — kliknij"
                onClick={projectId ? () => setNarzutModal("people") : undefined}
              />
              <CostItem
                label="Narzut — koszty stałe"
                value={summary.overhead_narzut_fixed}
                hint="globalne koszty stałe firmy wg wartości — kliknij"
                onClick={projectId ? () => setNarzutModal("fixed") : undefined}
              />
            </>
          )}
        </div>
      </div>

      {projectId && <EngagementCurveCard projectId={projectId} />}

      {projectId && narzutModal === "people" && (
        <NarzutPeopleModal
          projectId={projectId}
          total={summary.overhead_narzut_people}
          onClose={() => setNarzutModal(null)}
        />
      )}
      {projectId && narzutModal === "fixed" && (
        <NarzutFixedModal
          projectId={projectId}
          total={summary.overhead_narzut_fixed}
          onClose={() => setNarzutModal(null)}
        />
      )}
    </div>
  );
}

function EngagementCurveCard({ projectId }: { projectId: string }) {
  const { data } = useEngagementCurve(projectId);
  if (!data || !data.rows || data.rows.length === 0) return null;
  const chartData = data.rows.map((r) => ({
    label: `${NARZUT_MONTHS[r.month - 1]} ${String(r.year).slice(2)}`,
    activity: r.activity_pct,
    load: r.load,
    source: r.source,
  }));
  return (
    <div className="bg-white dark:bg-gray-800 p-3 sm:p-4 mt-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">
        Krzywa wygaszania (obciążenie kosztowe)
      </p>
      <p className="text-xs text-gray-400 mb-2 max-w-3xl">
        Obciążenie kosztowe projektu (Σ % etatu × pensja obsady, zł) miesiąc po
        miesiącu — fakt do dziś, dalej prognoza góry (te same dane co wykresy
        obłożenia). Wg tego ciężaru rozkładają się koszty stałe i nieprojektowi:
        tańsza/mniejsza obsada → mniejszy narzut. 100% = szczyt obciążenia (
        {formatAmount(data.peak_load.toFixed(2))} zł/mc).
      </p>
      <ResponsiveContainer width="100%" height={150}>
        <BarChart
          data={chartData}
          margin={{ top: 4, right: 8, left: -22, bottom: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} unit="%" />
          <Tooltip
            formatter={(v, _n, item) =>
              `${v}% · ${formatAmount((item?.payload?.load ?? 0).toFixed(2))} zł`
            }
          />
          <Bar dataKey="activity" name="Obciążenie" radius={[2, 2, 0, 0]}>
            {chartData.map((d, i) => (
              <Cell
                key={i}
                fill={d.source === "fact" ? "#2563eb" : "#f59e0b"}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <p className="text-[10px] text-gray-400 mt-1">
        Niebieski = fakt (PEA), pomarańczowy = prognoza.
      </p>
    </div>
  );
}

function CostItem({
  label,
  value,
  hint,
  onClick,
}: {
  label: string;
  value: string;
  hint: string;
  onClick?: () => void;
}) {
  const clickable = !!onClick;
  return (
    <div
      onClick={onClick}
      className={
        clickable
          ? "-m-1 p-1 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 transition"
          : undefined
      }
    >
      <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
        {label}
        {clickable && <Info size={12} className="text-gray-400" />}
      </p>
      <p className="text-base font-bold text-red-700 my-0.5">
        −{formatAmount(value)} PLN
      </p>
      <p className="text-xs text-gray-400 dark:text-gray-500">{hint}</p>
    </div>
  );
}

// ── Modale narzutu (rozbicie „skąd ta kwota") ───────────────────────────────
function NarzutModalShell({
  title,
  subtitle,
  total,
  onClose,
  children,
}: {
  title: string;
  subtitle: string;
  total: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-[34rem] max-w-full max-h-[85vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-1">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {title}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 shrink-0"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex items-baseline justify-between border-y border-gray-200 dark:border-gray-700 py-2 my-3">
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
            Razem
          </span>
          <span className="text-base font-bold tabular-nums text-red-700">
            −{formatAmount(total)} PLN
          </span>
        </div>
        {children}
      </div>
    </div>
  );
}

const NARZUT_MONTHS = [
  "sty",
  "lut",
  "mar",
  "kwi",
  "maj",
  "cze",
  "lip",
  "sie",
  "wrz",
  "paź",
  "lis",
  "gru",
];

function NarzutPeopleModal({
  projectId,
  total,
  onClose,
}: {
  projectId: string;
  total: string;
  onClose: () => void;
}) {
  const { data: rows = [], isLoading } = useNarzutPeopleBreakdown(projectId);
  // grupowanie po miesiącu (chronologicznie), z sumą per miesiąc
  const groups: {
    key: string;
    label: string;
    rows: typeof rows;
    sum: number;
  }[] = [];
  const idx = new Map<string, number>();
  for (const r of rows) {
    const key = `${r.year}-${r.month}`;
    if (!idx.has(key)) {
      idx.set(key, groups.length);
      groups.push({
        key,
        label: `${NARZUT_MONTHS[r.month - 1]} ${r.year}`,
        rows: [],
        sum: 0,
      });
    }
    const g = groups[idx.get(key)!];
    g.rows.push(r);
    g.sum += parseFloat(r.amount) || 0;
  }
  return (
    <NarzutModalShell
      title="Narzut — pracownicy nieprojektowi"
      subtitle="Pełne pensje pracowników nieprojektowych (kadry/księgowość/zarząd) rozłożone na projekt wg jego udziału wartościowego, miesiąc po miesiącu. Okres: od początku projektu DO DZIŚ (koszt już poniesiony) — przyszłe miesiące pojawią się w prognozie cashflow."
      total={total}
      onClose={onClose}
    >
      {isLoading ? (
        <p className="text-sm text-gray-400">Wczytuję…</p>
      ) : groups.length === 0 ? (
        <p className="text-sm text-gray-400">
          Brak pracowników nieprojektowych w okresie projektu.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-gray-400 text-left">
              <th className="py-1">Miesiąc / pracownik</th>
              <th className="py-1 text-right">Pełna pensja</th>
              <th className="py-1 text-right">% udziału</th>
              <th className="py-1 text-right">Narzut</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <Fragment key={g.key}>
                <tr className="bg-gray-50 dark:bg-gray-700/40">
                  <td
                    colSpan={4}
                    className="py-1 px-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500"
                  >
                    {g.label}
                  </td>
                </tr>
                {g.rows.map((r, i) => (
                  <tr
                    key={`${g.key}-${r.employee_name}-${i}`}
                    className="text-gray-600 dark:text-gray-300"
                  >
                    <td className="py-1.5 pl-3">{r.employee_name}</td>
                    <td className="py-1.5 text-right tabular-nums">
                      {formatAmount(r.full_salary)} PLN
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-gray-400">
                      {r.share_pct.toFixed(2)}%
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      −{formatAmount(r.amount)} PLN
                    </td>
                  </tr>
                ))}
                <tr className="font-semibold text-gray-800 dark:text-gray-100 border-t border-gray-200 dark:border-gray-600">
                  <td className="py-1 pl-3">Suma {g.label}</td>
                  <td></td>
                  <td></td>
                  <td className="py-1 text-right tabular-nums text-red-700">
                    −{formatAmount(g.sum.toFixed(2))} PLN
                  </td>
                </tr>
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </NarzutModalShell>
  );
}

function NarzutFixedModal({
  projectId,
  total,
  onClose,
}: {
  projectId: string;
  total: string;
  onClose: () => void;
}) {
  const { data: months = [], isLoading } = useNarzutFixedBreakdown(projectId);
  // dane przychodzą per miesiąc (wg OBŁOŻENIA); renderujemy miesiąc po miesiącu z sumą per miesiąc
  const groups = [...months]
    .sort((a, b) => a.year - b.year || a.month - b.month)
    .map((mo) => ({
      key: `${mo.year}-${mo.month}`,
      label: `${NARZUT_MONTHS[mo.month - 1]} ${mo.year}`,
      sum: parseFloat(mo.amount) || 0,
      items: [...mo.items].sort(
        (a, b) => (parseFloat(b.amount) || 0) - (parseFloat(a.amount) || 0),
      ),
    }));
  return (
    <NarzutModalShell
      title="Narzut — globalne koszty stałe"
      subtitle="Firmowe koszty cykliczne (nieprzypisane do projektu) rozłożone na projekt wg jego udziału wartościowego, miesiąc po miesiącu. Okres: od początku projektu DO DZIŚ — przyszłe miesiące w prognozie cashflow."
      total={total}
      onClose={onClose}
    >
      {isLoading ? (
        <p className="text-sm text-gray-400">Wczytuję…</p>
      ) : groups.length === 0 ? (
        <p className="text-sm text-gray-400">
          Brak globalnych kosztów stałych w okresie projektu.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-gray-400 text-left">
              <th className="py-1">Miesiąc / koszt</th>
              <th className="py-1 text-right">Udział %</th>
              <th className="py-1 text-right">Na projekcie</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <Fragment key={g.key}>
                <tr className="bg-gray-50 dark:bg-gray-700/40">
                  <td
                    colSpan={3}
                    className="py-1 px-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500"
                  >
                    {g.label}
                  </td>
                </tr>
                {g.items.map((it, i) => (
                  <tr
                    key={`${g.key}-${it.description}-${i}`}
                    className="text-gray-600 dark:text-gray-300"
                  >
                    <td className="py-1.5 pl-3">{it.description}</td>
                    <td className="py-1.5 text-right tabular-nums text-gray-400">
                      {it.fraction_pct.toFixed(1)}%
                    </td>
                    <td className="py-1.5 text-right tabular-nums">
                      −{formatAmount(it.amount)} PLN
                    </td>
                  </tr>
                ))}
                <tr className="font-semibold text-gray-800 dark:text-gray-100 border-t border-gray-200 dark:border-gray-600">
                  <td className="py-1 pl-3">Suma {g.label}</td>
                  <td></td>
                  <td className="py-1 text-right tabular-nums text-red-700">
                    −{formatAmount(g.sum.toFixed(2))} PLN
                  </td>
                </tr>
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </NarzutModalShell>
  );
}
